import { useState } from 'react';
import { motion } from 'framer-motion';

const NODE_FEATURES = [
  {
    icon: 'ri-store-2-line',
    color: '#00ff41',
    colorClass: 'neon-green',
    title: 'ショップ / SHOP',
    description: 'レコードショップ、ライブハウス、カフェ——街の音楽拠点がNODEとして登録。アーティストとリスナーをつなぐ物理的な接点。',
    tags: ['レコードショップ', 'ライブハウス', 'カフェ'],
  },
  {
    icon: 'ri-nfc-line',
    color: '#ff006e',
    colorClass: 'neon-pink',
    title: 'NFCタグ設置',
    description: 'NODEに設置されたNFCタグをスマホでタップ。その場所に紐づいたアーティストの音楽が即座に解放される。',
    tags: ['物理的接触', '即時再生', '場所連動'],
  },
  {
    icon: 'ri-map-pin-2-line',
    color: '#00d9ff',
    colorClass: 'neon-blue',
    title: '地域ネットワーク',
    description: '各都道府県のNODEが連携し、地域の音楽シーンを可視化。あなたの街のアンダーグラウンドを発見しよう。',
    tags: ['都道府県別', 'マップ表示', 'ローカルシーン'],
  },
];

const NODE_STATS = [
  { value: '47', label: '都道府県', suffix: '' },
  { value: '200', label: 'NODE拠点', suffix: '+' },
  { value: '1,500', label: 'アーティスト', suffix: '+' },
  { value: '50,000', label: 'タップ数/月', suffix: '+' },
];

export function NodeSection() {
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section id="node" className="relative py-16 lg:py-32 bg-[#080808] overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(#00ff41 1px, transparent 1px), linear-gradient(90deg, #00ff41 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* ── Fluid Ambient Glow ── */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 750,
          height: 750,
          background: 'radial-gradient(circle, rgba(0,255,65,0.13) 0%, transparent 70%)',
          filter: 'blur(80px)',
          top: '5%',
          left: '-15%',
        }}
        animate={{
          x: [0, 100, -60, 0],
          y: [0, -70, 50, 0],
          scale: [1, 1.18, 0.88, 1],
        }}
        transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 600,
          height: 600,
          background: 'radial-gradient(circle, rgba(0,255,65,0.09) 0%, transparent 70%)',
          filter: 'blur(100px)',
          bottom: '0%',
          right: '-10%',
        }}
        animate={{
          x: [0, -80, 50, 0],
          y: [0, 60, -40, 0],
          scale: [1, 0.85, 1.12, 1],
        }}
        transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(255,0,110,0.07) 0%, transparent 70%)',
          filter: 'blur(120px)',
          top: '40%',
          left: '40%',
        }}
        animate={{
          x: [0, 60, -80, 0],
          y: [0, -50, 60, 0],
          scale: [1, 1.15, 0.9, 1],
        }}
        transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut', delay: 10 }}
      />

      <div className="container mx-auto px-4 sm:px-8 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-24">
          <h2
            className="font-display text-4xl sm:text-6xl lg:text-9xl mb-4 lg:mb-8 tracking-wider"
            style={{
              color: '#00ff41',
              textShadow: '0 0 20px rgba(0,255,65,0.9), 0 0 40px rgba(0,255,65,0.5), 0 0 80px rgba(0,255,65,0.2)',
            }}
          >
            NODE
          </h2>
          <div
            className="w-24 lg:w-40 h-0.5 bg-neon-green mx-auto mb-6 lg:mb-10"
            style={{ boxShadow: '0 0 20px rgba(0,255,65,0.9), 0 0 50px rgba(0,255,65,0.3), 0 0 80px rgba(0,255,65,0.1)' }}
          />
          <p
            className="text-white text-xl lg:text-3xl font-bold mb-4 lg:mb-6 tracking-wide"
            style={{ textShadow: '0 0 30px rgba(0,255,65,0.6), 0 0 60px rgba(0,255,65,0.2)' }}
          >
            「流行」より、「聖域」を。
          </p>
          <p className="text-gray-400 text-sm lg:text-lg max-w-2xl mx-auto leading-relaxed lg:leading-loose">
            流行を遠ざけ、衝動の音を育み、誰かへと繋ぐ場所。<br />
            外の声を忘れ、ただ音に浸れる。<br className="hidden lg:block" />
            表現と足跡が重なり合い<strong className="text-white" style={{ color: '#00ff41', textShadow: '0 0 16px rgba(0,255,65,0.9), 0 0 32px rgba(0,255,65,0.3)' }}>「体温のある音」</strong>を優しく包み込む。
          </p>
        </div>

        {/* Main Content: Feature Tabs */}
        <div className="max-w-3xl mx-auto mb-16 lg:mb-28">

          {/* Feature List */}
          <div className="space-y-3 lg:space-y-4">
            {NODE_FEATURES.map((feature, i) => (
              <button
                key={i}
                onClick={() => setActiveFeature(i)}
                className={`w-full text-left p-5 lg:p-7 rounded-2xl border transition-all duration-400 cursor-pointer ${
                  activeFeature === i
                    ? 'border-white/30 bg-white/[0.05]'
                    : 'border-white/5 bg-transparent hover:border-white/15 hover:bg-white/[0.02]'
                }`}
                style={
                  activeFeature === i
                    ? { boxShadow: `0 0 30px ${feature.color}25, 0 0 60px ${feature.color}08` }
                    : {}
                }
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300"
                    style={{
                      background: activeFeature === i ? `${feature.color}20` : 'transparent',
                      border: `1.5px solid ${activeFeature === i ? feature.color : 'rgba(255,255,255,0.1)'}`,
                      boxShadow: activeFeature === i ? `0 0 20px ${feature.color}50, 0 0 40px ${feature.color}15` : 'none',
                    }}
                  >
                    <i
                      className={`${feature.icon} text-lg lg:text-xl`}
                      style={{ color: activeFeature === i ? feature.color : '#6b7280' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-sm lg:text-lg font-bold mb-1 lg:mb-2 transition-colors duration-300"
                      style={{ color: activeFeature === i ? '#ffffff' : '#9ca3af' }}
                    >
                      {feature.title}
                    </h3>
                    {activeFeature === i && (
                      <p className="text-gray-400 text-xs lg:text-sm leading-relaxed mb-3">
                        {feature.description}
                      </p>
                    )}
                    {activeFeature === i && (
                      <div className="flex flex-wrap gap-2">
                        {feature.tags.map((tag, ti) => (
                          <span
                            key={ti}
                            className="px-2 py-0.5 rounded-full text-[10px] lg:text-xs font-medium"
                            style={{
                              background: `${feature.color}15`,
                              border: `1px solid ${feature.color}40`,
                              color: feature.color,
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i
                      className={`ri-arrow-right-s-line text-lg transition-all duration-300 ${
                        activeFeature === i ? 'rotate-90' : ''
                      }`}
                      style={{ color: activeFeature === i ? NODE_FEATURES[activeFeature].color : '#374151' }}
                    />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* How to become a NODE */}
        <div className="max-w-5xl mx-auto">
          <div
            className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 lg:p-14 relative overflow-hidden"
            style={{ boxShadow: '0 0 60px rgba(0,255,65,0.07), 0 0 120px rgba(0,255,65,0.02)' }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-neon-green/5 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center gap-8 lg:gap-16">
              <div className="flex-1">
                <p className="text-neon-green text-xs tracking-[0.3em] uppercase mb-3">For Shop Owners</p>
                <h3 className="text-white text-2xl lg:text-4xl font-bold mb-4 lg:mb-6 leading-tight">
                  あなたの店を<br />NODEにしよう
                </h3>
                <p className="text-gray-400 text-sm lg:text-base leading-relaxed mb-6 lg:mb-8">
                  NFCタグを設置するだけで、あなたの店が音楽発見の拠点に。<br className="hidden lg:block" />
                  地域のアーティストと繋がり、新しいリスナーを呼び込む。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 lg:gap-4">
                  <a
                    href="/admin"
                    className="inline-flex items-center gap-2 px-6 lg:px-8 py-3 lg:py-4 bg-neon-green text-black font-bold text-sm lg:text-base rounded-full hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(0,255,65,0.5)] transition-all duration-300 whitespace-nowrap cursor-pointer no-underline"
                  >
                    <i className="ri-store-2-line" />
                    NODE登録する
                  </a>
                  <a
                    href="/directory"
                    className="inline-flex items-center gap-2 px-6 lg:px-8 py-3 lg:py-4 border border-white/20 text-white font-medium text-sm lg:text-base rounded-full hover:border-white/50 hover:-translate-y-1 transition-all duration-300 whitespace-nowrap cursor-pointer no-underline"
                  >
                    <i className="ri-map-2-line" />
                    NODEを探す
                  </a>
                </div>
              </div>

              {/* Steps */}
              <div className="w-full lg:w-72 space-y-4">
                {[
                  { step: '01', text: 'ショップアカウントを作成', icon: 'ri-user-add-line' },
                  { step: '02', text: 'NFCタグを受け取り設置', icon: 'ri-nfc-line' },
                  { step: '03', text: 'アーティストと連携開始', icon: 'ri-links-line' },
                ].map((s, si) => (
                  <div key={si} className="flex items-center gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-display text-sm"
                      style={{
                        background: 'rgba(0,255,65,0.1)',
                        border: '1px solid rgba(0,255,65,0.3)',
                        color: '#00ff41',
                        boxShadow: '0 0 15px rgba(0,255,65,0.25), 0 0 30px rgba(0,255,65,0.08)',
                      }}
                    >
                      {s.step}
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <i className={`${s.icon} text-gray-500 text-base`} />
                      <span className="text-gray-300 text-sm">{s.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
